import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bookmark, FileUp, IdCard, Search, Trash2, Upload } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchUsers, type AdminUserRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { ListBackLink, Body, ActionCard } from './userActionKit'
import {
  USERS_STR as S,
  loadSegments,
  saveSegment,
  deleteSegment,
  importUsers,
  isPending,
  type UserSegment,
  type ImportOutcome,
} from './usersAdminApi'

/**
 * A3.02 — Kengaytirilgan qidiruv (20+ filtr). JONLI `GET /admin/users` ustidan
 * X1 DataTable filtr-motori: har bir ustun `filterable` + operator to'plami
 * (contains/equals/startsWith/between/in) beради → yig'indida 20+ mezon. Yashirin
 * ustunlar (hiddenByDefault) ustun-menyusidan yoqiladi.
 */
export function AdvancedUserSearch() {
  const navigate = useNavigate()

  const columns = useMemo<AdminColumn<AdminUserRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id', width: 90, filterable: true },
      {
        key: 'name',
        header: 'Ism',
        type: 'text',
        minWidth: 160,
        sortable: false,
        accessor: (r) => [r.first_name, r.last_name].filter(Boolean).join(' '),
      },
      { key: 'first_name', header: 'Ism (first)', type: 'text', filterable: true, hiddenByDefault: true },
      { key: 'last_name', header: 'Familiya', type: 'text', filterable: true, hiddenByDefault: true },
      { key: 'username', header: 'Username', type: 'text', width: 150, filterable: true },
      {
        key: 'phone',
        header: 'Telefon',
        type: 'pii',
        piiLevel: 'L2',
        width: 160,
        sortable: false,
        filterable: false,
      },
      {
        key: 'status',
        header: 'Holat',
        type: 'enum',
        width: 130,
        filterable: true,
        options: [
          { value: 'active', label: 'Aktiv', status: 'active' },
          { value: 'banned', label: 'Bloklangan', status: 'banned' },
          { value: 'deleted', label: "O'chirilgan", status: 'deleted' },
        ],
      },
      { key: 'created_at', header: "Ro'yxatdan", type: 'date', width: 150, filterable: true },
      { key: 'last_seen', header: "Oxirgi faollik", type: 'date', width: 150, filterable: true, hiddenByDefault: true },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AdminUserRow>[]>(
    () => [{ id: 'card', label: 'Karta', icon: <IdCard size={13} />, onSelect: (r) => navigate(`/users/${r.id}`) }],
    [navigate],
  )

  const fetcher = useCallback(fetchUsers, [])

  return (
    <>
      <PageHeader title={S.searchTitle} screenId="A3.02" actions={<ListBackLink />} />
      <DataTable<AdminUserRow>
        screenId="A3.02"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        rowActions={rowActions}
        emptyState={{ icon: <Search size={28} strokeWidth={1.5} />, description: S.searchHint }}
      />
    </>
  )
}

/**
 * A3.03 — Saqlangan segmentlar. Filtr to'plamini nomlab saqlash → bir bosishда
 * qo'llash. Server segment API chiqmagunча mahalliy (localStorage) — halol
 * fail-soft (yo'qolmasligi uchun eksport A3.05'да mavjud).
 */
export function SavedSegments() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [segments, setSegments] = useState<UserSegment[]>(() => loadSegments())
  const [name, setName] = useState('')
  const [query, setQuery] = useState('')

  const add = () => {
    if (!name.trim()) return
    setSegments(saveSegment(name, query))
    setName('')
    setQuery('')
    toast(S.done, { tone: 'success' })
  }
  const remove = (id: string) => setSegments(deleteSegment(id))
  const open = (seg: UserSegment) =>
    navigate(`/users/search${seg.query ? `?q=${encodeURIComponent(seg.query)}` : ''}`)

  return (
    <>
      <PageHeader title={S.segTitle} screenId="A3.03" actions={<ListBackLink />} />
      <Body>
        <ActionCard hint={S.segLocalNote}>
          <label className="block">
            <span className="mb-1 block text-xs text-tertiary">{S.segName}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-tertiary">{S.segQuery}</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="masalan: @spam yoki +99890"
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
            />
          </label>
          <button
            onClick={add}
            disabled={!name.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            <Bookmark size={14} />
            {S.segSave}
          </button>
        </ActionCard>

        {segments.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <Bookmark size={24} className="mx-auto mb-2 opacity-60" />
            {S.segEmpty}
          </div>
        ) : (
          <ul className="space-y-2">
            {segments.map((seg) => (
              <li
                key={seg.id}
                className="flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-primary">{seg.name}</div>
                  {seg.query ? <div className="truncate font-mono text-xs text-tertiary">{seg.query}</div> : null}
                </div>
                <button
                  onClick={() => open(seg)}
                  className="shrink-0 rounded-md border border-border-default bg-surface-1 px-2.5 py-1 text-xs text-secondary hover:text-primary"
                >
                  {S.segOpen}
                </button>
                <button
                  onClick={() => remove(seg.id)}
                  className="shrink-0 rounded-md border border-border-default bg-surface-1 p-1.5 text-tertiary hover:text-danger"
                  aria-label={S.segDelete}
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Body>
    </>
  )
}

/**
 * A3.06 — Import (test muhiti uchun). CSV/JSON dan test foydalanuvchi. Prod
 * server RAD etadi (bu test-only). Greenfield → 404 bo'lsa «kutilmoqda».
 */
export function UserImport() {
  const { toast } = useToast()
  const [payload, setPayload] = useState('')
  const [dryRun, setDryRun] = useState(true)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ImportOutcome | null>(null)

  const run = async () => {
    if (!payload.trim()) return
    setBusy(true)
    setResult(null)
    try {
      const out = await importUsers(payload, dryRun)
      setResult(out)
      toast(S.importDone(out), { tone: 'success' })
    } catch (e) {
      if (isPending(e)) toast(S.pending, { tone: 'warning' })
      else toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.importTitle} screenId="A3.06" actions={<ListBackLink />} />
      <Body>
        <ActionCard hint={S.importHint}>
          <textarea
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            placeholder={'id,username,first_name\n1001,test_user,Test'}
            rows={8}
            className="w-full resize-y rounded-md border border-border-default bg-surface-1 px-3 py-2 font-mono text-xs text-primary outline-none focus:border-border-strong"
          />
          <label className="flex items-center gap-2 text-sm text-secondary">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            {S.importDryRun}
          </label>
          <button
            onClick={() => void run()}
            disabled={busy || !payload.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {dryRun ? <FileUp size={14} /> : <Upload size={14} />}
            {S.importBtn}
          </button>
          {result ? (
            <div className="rounded-md border border-border-default bg-surface-1 p-3 text-xs text-secondary">
              {S.importDone(result)}
              {result.errors?.length ? (
                <ul className="mt-1 list-disc pl-4 text-danger">
                  {result.errors.slice(0, 10).map((er, i) => (
                    <li key={i}>{er}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </ActionCard>
      </Body>
    </>
  )
}
